import type { GameState } from '../../engine/game-state.js';
import { createLayout, rowAbove } from '../../render/three/layout.js';
import type { GameSpec } from '../../render/three/game-app.js';
import {
  buildSpiderDeck,
  dealRow,
  FOUNDATION_IDS,
  isSameSuitDescendingRun,
  makeSpiderRules,
  type SpiderSuitCount,
  TABLEAU_IDS,
} from './rules.js';

const TABLEAU_Z = 0.0;
const TOP_Z = rowAbove(TABLEAU_Z);

// 10 tableau columns; top row holds the stock on the far left and the 8
// foundation slots clustered to the right. The unused column between stock
// and the foundations gives the eye some breathing room and keeps the
// foundations grouped as a single "graduated" zone.
const layout = createLayout({
  cols: 10,
  rows: [
    { z: TOP_Z, slots: ['stock', null, ...FOUNDATION_IDS] },
    { z: TABLEAU_Z, fanDown: true, slots: [...TABLEAU_IDS] },
  ],
});

function dragStackIds(state: GameState, cardId: string): string[] {
  for (const pile of Object.values(state.piles)) {
    const idx = pile.cards.findIndex((c) => c.id === cardId);
    if (idx === -1) continue;
    // Spider only drags from the tableau, and only same-suit descending runs.
    // (You can rest a 6♥ on a 7♠ during play, but you can't drag the pair as
    // a unit.) Stock/foundation are never drag sources.
    if (pile.type !== 'tableau') return [];
    const run = pile.cards.slice(idx);
    if (!isSameSuitDescendingRun(run)) return [];
    return run.map((c) => c.id);
  }
  return [];
}

export interface SpiderSpecOptions {
  suits?: SpiderSuitCount;
}

export function makeSpiderSpec(opts: SpiderSpecOptions = {}): GameSpec {
  const suits: SpiderSuitCount = opts.suits ?? 4;
  const rules = makeSpiderRules({ suits });
  return {
    rules,
    deckFactory: () => buildSpiderDeck(suits),
    slotForPile: (id) => layout.slot(id),
    // Stock is dealt via click; foundations only fill via auto-graduate.
    dropExclude: () => ['stock', ...FOUNDATION_IDS],
    dragStackIds,
    // No auto-send - every move is a deliberate placement in Spider.
    autoSendOrder: () => [],
    // Worst case: a 13-card K→A run rebuilt on top of an undealt column.
    // Keep generous padding for layout/camera so partial stacks fit cleanly.
    maxFanLength: 24,
    // Spider's 10-column row is the widest layout we have; the default 1.25×
    // global PAD pulls the camera back enough that the felt edge starts to
    // intrude on the frame. A modest tighten brings the cards to fill the
    // viewport (and the bumped table size keeps the felt covering it).
    cameraPad: 0.85,
    onPileClick(state, pile) {
      if (pile.id === 'stock') {
        if (!dealRow(state)) return false;
        state.movesCount++;
        return true;
      }
      return false;
    },
    statusText(state) {
      const stock = state.piles.stock!.cards.length;
      const dealsLeft = Math.floor(stock / TABLEAU_IDS.length);
      if (dealsLeft === 0) return null;
      return `${dealsLeft} deal${dealsLeft === 1 ? '' : 's'} left`;
    },
    cascadeCardIds(state) {
      const ids: string[] = [];
      for (const id of FOUNDATION_IDS) {
        for (const c of state.piles[id]!.cards) ids.push(c.id);
      }
      return ids;
    },
  };
}

export const spiderSpec: GameSpec = makeSpiderSpec({ suits: 4 });
